import * as React from 'react'
import { Box, CircularProgress, Snackbar, Typography, LinearProgress } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import SentMessagesPanel, {
  MessagesTab,
} from '@/components/ConsumerNotifications/SentMessagesPanel'
import {
  SENT_MESSAGES_PAGE_SIZE,
  useSentMessages,
} from '@/hooks/useSentMessages'
import AlertsFiltersBar, {
  ALERTS_DATE_TIME_FORMAT,
  AlertsFilters,
} from '@components/ConsumerNotifications/AlertsFiltersBar'
import AlertsManagePopover, {
  AlertsManageValues,
} from '@components/ConsumerNotifications/AlertsManagePopover'
import SendNotificationDialog from '@/components/notification-send/SendNotificationDialog'
import { toSendConsumer } from '@/components/notification-send/sendConsumer'
import columnsConfig from '@/components/features/config/columns/consumers/notification.column.config'
import { useTableStore } from '@/store/table/table'
import { useParams, useOutletContext } from 'react-router-dom'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  getConsumerNotifications,
  getConsumerSentMessages,
  getAlertSentMessages,
  getAlertTypes,
  exportConsumerNotificationsExcel,
  NotificationAlert,
  AlertType,
} from '@/services/notificationService'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { getServerPaginationUpdate, toApiPage } from '@/utils/serverPagination'
import { useConsumerPremise } from '@/hooks/useConsumerPremise'
import { describeGatewayError } from '@/utils/gatewayError'
import PageStateMessage from '@/components/common/PageStateMessage'
import { useAppTheme } from '@/theme/ThemeContext'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

/** A zero-based grid page and its size. */
type Paging = { page: number; pageSize: number }

// The Figma rows are 61px with 15px text; the funnel and the three-dot menu
// show on every header, and a clicked cell keeps no focus ring on the tinted row.
const ALERTS_ROW_HEIGHT = 61
const ALERTS_GRID_SX: SxProps<Theme> = {
  '& .MuiDataGrid-cell': { fontSize: '15px' },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '15px' },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
    width: 'auto',
    opacity: 1,
    visibility: 'visible',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
}

/** The filter fields are in the browser's zone; the Gateway gets instants. */
const toInstantWindow = (filters: AlertsFilters, fallback: AlertsFilters) => ({
  from: dayjs(filters.from || fallback.from, ALERTS_DATE_TIME_FORMAT).toDate(),
  to: dayjs(filters.to || fallback.to, ALERTS_DATE_TIME_FORMAT).toDate(),
})

const ConsumerNotifications: React.FC = () => {
  const { consumerId } = useParams()
  const accountId = localStorage.getItem('accountId') || ''
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [rows, setRows] = useState<(NotificationAlert & { id: number })[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() => getTablePageSize(50))
  const [loading, setLoading] = useState(false)
  const [alertsFailed, setAlertsFailed] = useState(false)
  // The alerts tab resolves the premise from the meter number on its own, so a
  // failure on another tab neither blocks it nor leaves it spinning.
  const premise = useConsumerPremise(accountId, consumerId)
  const premiseId = premise.premiseId
  const columns = columnsConfig
  const { setDateFilter } = useTableStore()

  const [openComposer, setOpenComposer] = React.useState(false)
  const [defaults] = useState<AlertsFilters>(() => ({
    from: dayjs()
      .subtract(1, 'month')
      .startOf('day')
      .format(ALERTS_DATE_TIME_FORMAT),
    to: dayjs().endOf('day').format(ALERTS_DATE_TIME_FORMAT),
    showClosed: false,
    showNotification: false,
  }))
  const [filters, setFilters] = React.useState<AlertsFilters>(defaults)
  // The filters of the last search; a page change must not apply unsearched edits.
  const [appliedFilters, setAppliedFilters] = React.useState(filters)
  const [manageAnchor, setManageAnchor] = React.useState<HTMLElement | null>(
    null
  )
  const [manageValues, setManageValues] = React.useState<AlertsManageValues>(() => {
    if (consumerId) {
      const saved = localStorage.getItem(`consumer-settings-${consumerId}`)
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error(e)
        }
      }
    }
    return {
      mode: 'enabled',
      until: '',
      alertType: 'all',
      messagesMode: 'enabled',
      messagesUntil: '',
      messagesAlertType: 'all',
      alertsMode: 'enabled',
      alertsUntil: '',
      alertsAlertType: 'all',
    }
  })

  useEffect(() => {
    if (consumerId && manageValues) {
      localStorage.setItem(`consumer-settings-${consumerId}`, JSON.stringify(manageValues))
    }
  }, [consumerId, manageValues])

  // A failed load leaves the pickers with "all", which is still a valid choice.
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([])
  useEffect(() => {
    if (!accountId) return
    getAlertTypes(accountId).then(setAlertTypes, () => setAlertTypes([]))
  }, [accountId])

  const [messagesTab, setMessagesTab] = useState<MessagesTab>('general')
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null)

  // Outlet context for Excel export (from ConsumerDetailsLayout)
  const { setExcelExportHandler } = useOutletContext<{ setExcelExportHandler?: (fn: (() => void) | null) => void }>() ?? {}

  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  // We need a local Snackbar for progress since ConsumerDetailsLayout doesn't have one for this
  const [isExporting, setIsExporting] = React.useState(false)
  const [exportProgress, setExportProgress] = React.useState(0)

  const handleExcelExport = useCallback(async () => {
    if (!accountId || !premiseId) return

    setIsExporting(true)
    setExportProgress(0)
    stopProgressTimer()

    let simulated = 0
    progressTimerRef.current = setInterval(() => {
      simulated += (90 - simulated) * 0.05
      setExportProgress((prev) => Math.min(Math.round(Math.max(prev, simulated)), 90))
    }, 300)

    const onProgress = (percent: number) => {
      setExportProgress((prev) => Math.max(prev, percent))
    }

    try {
      await exportConsumerNotificationsExcel(
        accountId,
        premiseId,
        {
          ...toInstantWindow(filters, defaults),
          includeClosed: filters.showClosed,
        },
        onProgress
      )

      stopProgressTimer()
      setExportProgress(100)
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (err) {
      console.error('Excel export failed:', err)
    } finally {
      stopProgressTimer()
      setIsExporting(false)
      setExportProgress(0)
    }
  }, [accountId, premiseId, filters, defaults, stopProgressTimer])

  // Register export handler with parent layout
  useEffect(() => {
    if (setExcelExportHandler) {
      setExcelExportHandler(() => handleExcelExport)
      return () => setExcelExportHandler(null)
    }
  }, [handleExcelExport, setExcelExportHandler])

  const handleRowClick = useCallback((row: { id: number }) => {
    setSelectedAlertId(row.id)
    setMessagesTab('alert')
  }, [])

  const fetchNotifications = useCallback(
    async (searchFilters: AlertsFilters, paging: Paging) => {
      if (!accountId || !premiseId) return

      setLoading(true)
      setAlertsFailed(false)
      try {
        const response = await getConsumerNotifications(accountId, premiseId, {
          ...toInstantWindow(searchFilters, defaults),
          includeClosed: searchFilters.showClosed,
          page: toApiPage(paging.page),
          size: paging.pageSize,
        })

        const mapped = (response.result || []).map(alert => ({
          ...alert,
          id: alert.alertId,
        }))

        // The service answers a failed request with an empty page; an empty list must not stand for it.
        setAlertsFailed(Boolean(response.failed))
        setRows(mapped)
        setTotal(response.total ?? 0)
      } catch (error) {
        console.error(
          '[ConsumerNotifications] Failed to fetch notifications:',
          error
        )
        setAlertsFailed(true)
      } finally {
        setLoading(false)
      }
    },
    [accountId, premiseId, defaults]
  )

  // Auto-fetch when premiseId becomes available
  useEffect(() => {
    if (premiseId) {
      fetchNotifications(filters, { page: 0, pageSize })
    }
  }, [premiseId])

  const handleSearch = useCallback(
    (searchFilters: AlertsFilters) => {
      if (searchFilters.from || searchFilters.to) {
        setDateFilter({
          from: searchFilters.from,
          to: searchFilters.to,
        })
      } else {
        setDateFilter(null)
      }
      setAppliedFilters(searchFilters)
      setPage(0)
      fetchNotifications(searchFilters, { page: 0, pageSize })
    },
    [setDateFilter, fetchNotifications, pageSize]
  )

  // The closed-alerts box acts at once; the dates still wait for the search button.
  const handleFiltersChange = useCallback(
    (next: AlertsFilters) => {
      setFilters(next)
      if (next.showClosed !== filters.showClosed) handleSearch(next)
    },
    [filters.showClosed, handleSearch]
  )

  const handlePageChange = useCallback(
    (newPage: number, newPageSize?: number) => {
      const next = getServerPaginationUpdate(
        pageSize,
        newPage,
        newPageSize ?? pageSize
      )
      setPage(next.page)
      setPageSize(next.pageSize)
      fetchNotifications(appliedFilters, next)
    },
    [appliedFilters, fetchNotifications, pageSize]
  )

  // "כללי" follows the window of the last search, like the alert list.
  const appliedWindow = useMemo(
    () => toInstantWindow(appliedFilters, defaults),
    [appliedFilters, defaults]
  )
  const loadGeneral = useMemo(
    () =>
      accountId && premiseId
        ? (page: number) =>
            getConsumerSentMessages(
              accountId,
              premiseId,
              { page, size: SENT_MESSAGES_PAGE_SIZE },
              appliedWindow
            )
        : null,
    [accountId, premiseId, appliedWindow]
  )
  const generalList = useSentMessages(
    messagesTab === 'general' ? loadGeneral : null,
    `${premiseId}|${appliedWindow.from.getTime()}|${appliedWindow.to.getTime()}`
  )
  const alertList = useSentMessages(
    messagesTab === 'alert' && accountId && selectedAlertId !== null
      ? (page: number) =>
          getAlertSentMessages(accountId, selectedAlertId, {
            page,
            size: SENT_MESSAGES_PAGE_SIZE,
          })
      : null,
    String(selectedAlertId)
  )

  // Distinct name (not `accountId`) to avoid shadowing the string accountId
  // used for API calls elsewhere in this file. Guarded like the pattern in
  // src/pages/Consumers.tsx: read the raw value first so a missing/invalid
  // accountId becomes NaN instead of a silent 0 (Number(null) === 0) that
  // could reach the dialog as if it were a real account id.
  const notificationAccountIdRaw = localStorage.getItem('accountId')
  const notificationAccountId = notificationAccountIdRaw
    ? Number(notificationAccountIdRaw)
    : NaN

  // Mirrors the accountId guard: only open the composer once
  // notificationAccountId is valid AND premiseId has resolved from the
  // async getSingleConsumer fetch above. Otherwise a click during that
  // fetch would open the dialog with an empty audience (ids: []).
  const handleOpenComposer = () => {
    if (!notificationAccountId || premiseId === null) return
    setOpenComposer(true)
  }

  // Drives NotificationsPanel's existing enableAdd prop so the trigger
  // shows a visible not-allowed state (see HeaderButton) until both ids
  // are ready, instead of silently no-oping on click.
  const composerReady = Boolean(notificationAccountId) && premiseId !== null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        minHeight: 'calc(100dvh - 180px)',
        pt: 4,
        height: '100%',
      }}
    >
      <AlertsFiltersBar
        value={filters}
        onChange={handleFiltersChange}
        onSearch={handleSearch}
        alertsMode={
          manageValues.alertsMode === 'muted' || manageValues.messagesMode === 'muted'
            ? 'muted'
            : 'enabled'
        }
        onOpenNotifications={el => setManageAnchor(el)}
        showCheckBox={true}
        openManagePopover={manageAnchor}
        variant="meterPage"
      />
      <Box
        sx={{
          display: 'flex',
          // Below md the messages go under the table instead of squeezing it.
          flexDirection: { xs: 'column', md: 'row' },
          gap: 'clamp(12px, 1.25vw, 24px)',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
          minHeight: { md: 0 },
          flex: { xs: '0 0 auto', md: 1 },
          mt: 3,
        }}
      >
        <Box
          sx={{
            flex: { xs: '0 0 auto', md: '0 1 clamp(240px, 23vw, 370px)' },
            minWidth: { md: 'clamp(240px, 16vw, 320px)' },
            minHeight: { xs: 320, md: 0 },
            order: { xs: 1, md: 0 },
          }}
        >
          <SentMessagesPanel
            tab={messagesTab}
            onTabChange={setMessagesTab}
            alertSelected={selectedAlertId !== null}
            list={messagesTab === 'general' ? generalList : alertList}
            onAdd={handleOpenComposer}
            enableAdd={composerReady}
          />
        </Box>
        {premise.status === 'error' ? (
          <Box
            sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}
          >
            <PageStateMessage
              tone="error"
              title={
                consumerId
                  ? `לא ניתן לטעון התראות עבור מד ${consumerId}`
                  : 'לא ניתן לטעון התראות'
              }
              detail={describeGatewayError(
                premise.error,
                'טעינת פרטי המד נכשלה'
              )}
              onRetry={premise.reload}
            />
          </Box>
        ) : alertsFailed && !loading ? (
          <Box
            sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}
          >
            <PageStateMessage
              tone="error"
              title={
                consumerId
                  ? `לא ניתן לטעון התראות עבור מד ${consumerId}`
                  : 'לא ניתן לטעון התראות'
              }
              detail="טעינת ההתראות נכשלה"
              onRetry={() => fetchNotifications(appliedFilters, { page, pageSize })}
            />
          </Box>
        ) : loading || premise.status === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 'clamp(360px, calc(100dvh - 390px), 900px)' }}>
            <DataGridPremiumDemo
              width="100%"
              columns={columns}
              rows={rows}
              hideFooter
              headerPosition="bottom"
              pageSize={pageSize}
              paginationMode="server"
              serverSideRowCount={total}
              serverSidePage={page}
              onPaginationChange={handlePageChange}
              onRowClick={handleRowClick}
              onRowDoubleClick={handleRowClick}
              maxHeight="100%"
              autoRowHeight
              rowHeight={ALERTS_ROW_HEIGHT}
              gridSx={ALERTS_GRID_SX}
            />
          </Box>
        )}
      </Box>

      <AlertsManagePopover
        anchorEl={manageAnchor}
        open={Boolean(manageAnchor)}
        onClose={() => setManageAnchor(null)}
        values={manageValues}
        onChange={v => setManageValues(v)}
        alertTypes={alertTypes}
        variant="meterPage"
        onSave={v => {
          setManageValues(v)
          setManageAnchor(null)
        }}
      />
      <SendNotificationDialog
        open={openComposer}
        onClose={() => setOpenComposer(false)}
        accountId={notificationAccountId}
        audience={{
          kind: 'premises',
          ids: premiseId !== null ? [premiseId] : [],
        }}
        consumer={
          premise.consumer ? toSendConsumer(premise.consumer) : undefined
        }
      />

      {/* Export progress indicator */}
      <Snackbar
        open={isExporting}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: isDarkMode ? '#1D325A' : '#fff',
            color: isDarkMode ? '#fff' : '#003F61',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 300,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                מייצא קובץ Excel...
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'left' }}>
                {exportProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{
                borderRadius: 1,
                height: 6,
                bgcolor: isDarkMode ? '#374151' : '#E4E7EB',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#81B5FF',
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        </Box>
      </Snackbar>
    </Box>
  )
}

export default ConsumerNotifications
